/**
 * App Insights.
 * window.AppInsights is available globally via loader.
 *
 * See: https://github.com/microsoft/ApplicationInsights-JS
 */

import type { ITelemetryItem } from '@microsoft/applicationinsights-web';
import debounce from 'lodash/debounce';
import { AnalyticsEventMap, attachPageUrlToEvent } from './customEvents';
import { attachMetadataToException } from './exceptions';
import { env } from '@/env';

export function initAppInsights() {
  window.appInsights?.addTelemetryInitializer(attachAppVersion);
  window.appInsights?.addTelemetryInitializer(attachPageUrlToEvent);
  window.appInsights?.addTelemetryInitializer(attachMetadataToException);
}

/**
 * Send page view event with debounce.
 * Reason: Algolia widgets perform several updates of URL.
 */
export const trackPageViewDebounce = debounce(() => {
  window.appInsights?.trackPageView();
}, 500);

/**
 * Send custom event.
 * Having event name as a first parameter leads to more concise consumer code.
 *
 * E.g.:
 * trackEvent('Search.Query', { query: 'foo' });
 * trackEvent('Search.ClearAll');
 */
export const trackEvent = <T extends keyof AnalyticsEventMap>(
  name: T,
  // if event doesn't have extra props, do not require second arg
  // if event has extra props, require second arg
  ...args: keyof AnalyticsEventMap[T] extends never ? [] : [AnalyticsEventMap[T]]
) => {
  window.appInsights?.trackEvent({ name }, args[0]);
};

/**
 * Set user email for analytics events.
 * See: https://learn.microsoft.com/en-us/azure/azure-monitor/app/api-custom-events-metrics#authenticated-users
 */
export function setUserId(userId?: string) {
  window.appInsights?.setAuthenticatedUserContext(userId || '');
}

/**
 * Track exception.
 */
export function trackException(error: Error | string) {
  const exception = typeof error === 'string' ? new Error(error) : error;
  const digest = 'digest' in exception ? (exception.digest as string) : undefined;
  window.appInsights?.trackException({ exception }, { digest });
}

function attachAppVersion(envelope: ITelemetryItem) {
  if (envelope.tags) {
    // see: https://github.com/microsoft/ApplicationInsights-JS/issues/785
    envelope.tags['ai.application.ver'] = env.build.version;
  }
}
